import React, { useEffect, useState } from "react";
import { LockKeyhole, UserRound } from "lucide-react";
import { AcaiLogo } from "@/components/AcaiLogo";
import { OpeningScreen } from "@/components/OpeningScreen";
import { api, recoverLocalOwner, setupLocalOwner } from "@/lib/apiClient";
import { userError } from "@/lib/userError";

export const AuthGate = ({ children }) => {
  const [state, setState] = useState("checking");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [recoveryAvailable, setRecoveryAvailable] = useState(false);
  const [error, setError] = useState("");
  const [checkAttempt, setCheckAttempt] = useState(0);
  useEffect(()=>{const expired=()=>{setState('login');setPassword('');setPasswordConfirm('');setError('Istunto päättyi. Kirjaudu uudelleen.');};window.addEventListener('acai-session-expired',expired);return()=>window.removeEventListener('acai-session-expired',expired);},[]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    api.get("/auth/status", {signal: controller.signal})
      .then((response) => {
        if (!active) return;
        if (typeof response.data?.authenticated !== "boolean") throw new Error("Invalid authentication response");
        setRecoveryAvailable(response.data.recovery_available === true);
        setState(response.data.authenticated ? "authenticated" : response.data.setup_required === true ? "setup" : "login");
      })
      .catch((requestError) => {
        if (!active) return;
        if (requestError?.response?.status === 401) {
          setRecoveryAvailable(false);
          setState("login");
        } else {
          setError(requestError?.code === "ECONNABORTED" || requestError?.response?.status === 504
            ? "Palvelu ei vastannut kirjautumistilan tarkistukseen ajoissa. Odota hetki ja tarkista yhteys uudelleen."
            : userError(requestError, "Palvelinyhteyttä ei voitu tarkistaa. Tarkista verkkoyhteys ja yritä uudelleen."));
          setState("connection-error");
        }
      });
    return () => { active = false; controller.abort(); };
  }, [checkAttempt]);

  const retryCheck = () => {
    setError("");
    setState("checking");
    setCheckAttempt(attempt => attempt + 1);
  };

  const login = async (event) => {
    event.preventDefault();
    setError("");
    setState("submitting");
    try {
      await api.post("/auth/login", { email, password });
      setPassword("");
      setState("authenticated");
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Kirjautuminen epäonnistui.");
      setState("login");
    }
  };

  const recoverOwner = async (event) => {
    event.preventDefault();
    setError("");
    if (password !== passwordConfirm) {
      setError("Salasanat eivät täsmää.");
      return;
    }
    setState("submitting-recovery");
    try {
      await recoverLocalOwner({ email, password });
      setPassword("");
      setPasswordConfirm("");
      setRecoveryAvailable(false);
      setState("login");
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Salasanan vaihtaminen epäonnistui.");
      setState("recovery");
    }
  };

  const setupOwner = async (event) => {
    event.preventDefault();
    setError("");
    if (password !== passwordConfirm) {
      setError("Salasanat eivät täsmää.");
      return;
    }
    setState("submitting-setup");
    try {
      await setupLocalOwner({ name, email, password });
      setPassword("");
      setPasswordConfirm("");
      setState("login");
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Pääkäyttäjän luominen epäonnistui.");
      setState("setup");
    }
  };

  if (state === "authenticated") return <><OpeningScreen loading={false} />{children}</>;

  if (state === "checking") {
    return <><OpeningScreen loading /><div className="auth-loading" aria-live="polite">Avataan assistenttia…</div></>;
  }

  if (state === "connection-error") {
    return <><OpeningScreen loading={false} /><main className="auth-screen"><div className="auth-card">
      <AcaiLogo />
      <h1>Yhteyttä ei voitu varmistaa</h1>
      <p role="alert">{error}</p>
      <p>Kirjautumistilaasi ei vielä voitu tarkistaa. Tämä ei tarkoita, että olisit kirjautunut ulos.</p>
      <button type="button" onClick={retryCheck}>Tarkista yhteys uudelleen</button>
    </div></main></>;
  }

  return (<><OpeningScreen loading={false} />
    <main className="auth-screen">
      <div className="auth-card">
        <AcaiLogo />
        <img className="auth-orb" src="/assets/voice-orb-glass-v1.png" alt="" />
        {state === "recovery" || state === "submitting-recovery" ? (
          <>
            <div>
              <h1>Vaihda salasana</h1>
              <p>Aseta uusi salasana tälle paikalliselle pääkäyttäjälle.</p>
            </div>
            <form onSubmit={recoverOwner}>
              <label htmlFor="recovery-email">Pääkäyttäjän sähköposti</label>
              <div className="auth-input-wrap">
                <LockKeyhole aria-hidden="true" />
                <input id="recovery-email" type="email" inputMode="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoFocus />
              </div>
              <label htmlFor="recovery-password">Uusi salasana</label>
              <div className="auth-input-wrap">
                <LockKeyhole aria-hidden="true" />
                <input id="recovery-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={12} maxLength={128} autoComplete="new-password" required />
              </div>
              <label htmlFor="recovery-password-confirm">Uusi salasana uudelleen</label>
              <div className="auth-input-wrap">
                <LockKeyhole aria-hidden="true" />
                <input id="recovery-password-confirm" type="password" value={passwordConfirm} onChange={(event) => setPasswordConfirm(event.target.value)} minLength={12} maxLength={128} autoComplete="new-password" required />
              </div>
              <small>Vähintään 12 merkkiä. Tallenna uusi salasana ennen jatkamista.</small>
              {error && <p className="auth-error" role="alert">{error}</p>}
              <button type="submit" disabled={state === "submitting-recovery" || !email || password.length < 12 || passwordConfirm.length < 12}>
                {state === "submitting-recovery" ? "Vaihdetaan…" : "Vaihda salasana"}
              </button>
              <button type="button" className="auth-secondary" onClick={() => { setError(""); setPassword(""); setPasswordConfirm(""); setState("login"); }}>
                Takaisin kirjautumiseen
              </button>
            </form>
          </>
        ) : state === "setup" || state === "submitting-setup" ? (
          <>
            <div>
              <h1>Luo pääkäyttäjä</h1>
              <p>Tämä tehdään kerran tällä laitteella. Salasana ei poistu sovelluksesta.</p>
            </div>
            <form onSubmit={setupOwner}>
              <label htmlFor="owner-name">Nimi</label>
              <div className="auth-input-wrap">
                <UserRound aria-hidden="true" />
                <input id="owner-name" value={name} onChange={(event) => setName(event.target.value)} minLength={2} maxLength={120} autoComplete="name" required autoFocus />
              </div>
              <label htmlFor="owner-email">Sähköposti</label>
              <div className="auth-input-wrap">
                <LockKeyhole aria-hidden="true" />
                <input id="owner-email" type="email" inputMode="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
              </div>
              <label htmlFor="owner-password">Salasana</label>
              <div className="auth-input-wrap">
                <LockKeyhole aria-hidden="true" />
                <input id="owner-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={12} maxLength={128} autoComplete="new-password" required />
              </div>
              <label htmlFor="owner-password-confirm">Salasana uudelleen</label>
              <div className="auth-input-wrap">
                <LockKeyhole aria-hidden="true" />
                <input id="owner-password-confirm" type="password" value={passwordConfirm} onChange={(event) => setPasswordConfirm(event.target.value)} minLength={12} maxLength={128} autoComplete="new-password" required />
              </div>
              <small>Vähintään 12 merkkiä. Käytä vain tähän sovellukseen tarkoitettua salasanaa.</small>
              {error && <p className="auth-error" role="alert">{error}</p>}
              <button type="submit" disabled={state === "submitting-setup" || name.trim().length < 2 || !email || password.length < 12 || passwordConfirm.length < 12}>
                {state === "submitting-setup" ? "Luodaan…" : "Luo pääkäyttäjä"}
              </button>
            </form>
          </>
        ) : (
          <>
        <div>
          <h1>Oma assistentti</h1>
          <p>Kirjaudu omalla yrityskäyttäjälläsi.</p>
        </div>
        <form onSubmit={login}>
          <label htmlFor="user-email">Sähköposti</label>
          <div className="auth-input-wrap">
            <LockKeyhole aria-hidden="true" />
            <input
              id="user-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoFocus
            />
          </div>
          <label htmlFor="user-password">Salasana</label>
          <div className="auth-input-wrap">
            <LockKeyhole aria-hidden="true" />
            <input
              id="user-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              minLength={8}
              maxLength={128}
              required
            />
          </div>
          {error && <p className="auth-error" role="alert">{error}</p>}
          <button type="submit" disabled={state === "submitting" || !email || password.length < 8}>
            {state === "submitting" ? "Tarkistetaan…" : "Avaa assistentti"}
          </button>
        </form>
        {recoveryAvailable && (
          <button type="button" className="auth-secondary" onClick={() => { setError(""); setPassword(""); setState("recovery"); }}>
            Unohtuiko salasana? Vaihda se tässä
          </button>
        )}
        <small>Ei avointa rekisteröitymistä. Käyttäjät lisätään vain yrityksen kutsulla.</small>
          </>
        )}
      </div>
    </main></>
  );
};
